import axios from 'axios'
import type { AxiosError, AxiosRequestConfig } from 'axios'
import { ElMessage } from 'element-plus'

/**
 * 统一 axios 实例：baseURL=/api，请求自动携带 Bearer Token，
 * 401 自动清空本地会话并跳转登录页，业务错误用 ElMessage 反馈。
 *
 * 为什么单独封装：避免每个接口重复处理鉴权头与错误提示，
 * 让 api/ 层只关心“调用哪个接口、返回什么类型”。
 */
const request = axios.create({
  baseURL: '/api',
  timeout: 10000
})

/** 把各类错误归一成一条可读中文提示（带接口地址，便于定位问题）。 */
function resolveErrorMessage(error: AxiosError): string {
  const backendMessage = (error.response?.data as { message?: string } | undefined)?.message
  if (backendMessage) return backendMessage

  const status = error.response?.status
  const url = error.config?.url ?? ''
  if (status === 404) return `接口不存在（404）：${url}`
  if (status === 500) return '服务器内部错误（500）'
  if (status) return `请求失败（${status}）：${url}`
  if (error.code === 'ECONNABORTED') return '请求超时，请稍后重试'
  return error.message || '请求失败'
}

request.interceptors.request.use((config) => {
  const token = localStorage.getItem('token')
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

request.interceptors.response.use(
  (response) => response.data,
  (error: AxiosError) => {
    // 控制台保留完整错误与请求地址，方便排查 404 等接口问题
    console.error('[请求失败]', error.response?.status, error.config?.url, error)
    ElMessage.error(resolveErrorMessage(error))
    if (error.response?.status === 401) {
      localStorage.removeItem('token')
      localStorage.removeItem('user')
      if (window.location.pathname !== '/login') {
        // 携带当前路径作为 redirect，登录成功后可回到原页面
        const redirect = encodeURIComponent(
          window.location.pathname + window.location.search + window.location.hash
        )
        window.location.href = `/login?redirect=${redirect}`
      }
    }
    return Promise.reject(error)
  }
)

export default request

/**
 * 带类型的请求封装：axios 实例经响应拦截器后返回的是 response.data，
 * 这里把泛型套在 data 上，让 api/ 层拿到正确的返回类型。
 */
export const http = {
  get: <T>(url: string, config?: AxiosRequestConfig) =>
    request.get(url, config) as unknown as Promise<T>,
  post: <T>(url: string, data?: unknown, config?: AxiosRequestConfig) =>
    request.post(url, data, config) as unknown as Promise<T>,
  put: <T>(url: string, data?: unknown, config?: AxiosRequestConfig) =>
    request.put(url, data, config) as unknown as Promise<T>,
  delete: <T>(url: string, config?: AxiosRequestConfig) =>
    request.delete(url, config) as unknown as Promise<T>
}
